import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * PWA install prompt button.
 *
 * Listens to the browser's `beforeinstallprompt` event and displays a discrete
 * "Installer l'application" button when the app is installable (Chrome, Edge,
 * Samsung Internet on Android/desktop, iOS 16+ Safari has its own flow).
 *
 * Auto-hides after install or if the browser doesn't fire the event
 * (e.g. iOS Safari, already installed, or FF where PWA install isn't native).
 *
 * Props:
 *   variant: "navbar" | "hero" | "menu"  — visual style / size
 *   className: extra classes
 *   onInstalled: optional callback called after user accepts install
 */
export default function PWAInstallButton({ variant = "navbar", className = "", onInstalled }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // If the app is already running as an installed PWA (display-mode: standalone),
    // hide the button entirely.
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      onInstalled?.();
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [onInstalled]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setInstalled(true);
      }
    } finally {
      setDeferredPrompt(null);
    }
  };

  // Do not render anything if the browser hasn't fired the install prompt event
  // (Firefox, iOS Safari, already installed…). This keeps the UI clean.
  if (installed || !deferredPrompt) return null;

  if (variant === "hero") {
    return (
      <Button
        onClick={handleInstall}
        data-testid="pwa-install-hero"
        className={`bg-[#E5CD96] text-[#3F3F3F] hover:bg-[#B8A478] font-semibold rounded-none ${className}`}
      >
        <Download className="w-4 h-4 mr-2" /> Installer l&apos;application
      </Button>
    );
  }
  if (variant === "menu") {
    return (
      <button
        type="button"
        onClick={handleInstall}
        data-testid="pwa-install-menu"
        className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F0E9F3] flex items-center gap-2 ${className}`}
      >
        <Download className="w-4 h-4 text-[#662483]" />
        <span>Installer sur cet appareil</span>
      </button>
    );
  }
  // Default: navbar variant — small pill/outline
  return (
    <Button
      onClick={handleInstall}
      data-testid="pwa-install-navbar"
      variant="outline"
      size="sm"
      className={`rounded-none border-[#662483] text-[#662483] hover:bg-[#F0E9F3] h-8 text-xs ${className}`}
    >
      <Download className="w-3.5 h-3.5 mr-1" /> Installer l&apos;app
    </Button>
  );
}
